/**
 * Display formatting, driven by the active product locale (ADR-076).
 *
 * Default is `en-GB` rather than `en-US`: the pilot is deployed in Argentina,
 * where a date written 03/04 means the third of April. `es-AR` uses the same
 * day-month order. Formatters are rebuilt when the locale changes.
 *
 * Times are rendered in the browser's own time zone rather than the
 * organization's. For the pilot in Córdoba the two are the same, and showing
 * a reading at a time that does not match the wall clock in front of the
 * operator would be worse than the edge case it fixes. Honouring
 * `Organization.timezone` is a to-do for when a customer works across zones.
 */

import i18n, { getAppLocale } from "@/i18n";
import type { AppLocale } from "@/i18n/locales";

export const EM_DASH = "—";

interface Formatters {
  locale: AppLocale;
  dateTime: Intl.DateTimeFormat;
  time: Intl.DateTimeFormat;
  date: Intl.DateTimeFormat;
  dayMonth: Intl.DateTimeFormat;
  relative: Intl.RelativeTimeFormat;
  number: Intl.NumberFormat;
}

let cached: Formatters | null = null;

function formatters(): Formatters {
  const locale = getAppLocale();

  if (cached?.locale === locale) {
    return cached;
  }

  cached = {
    locale,
    dateTime: new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }),
    time: new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }),
    date: new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    }),
    dayMonth: new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit" }),
    relative: new Intl.RelativeTimeFormat(locale, { numeric: "auto" }),
    number: new Intl.NumberFormat(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }),
  };

  return cached;
}

/** Drop cached Intl instances after a language switch. */
export function resetFormatters(): void {
  cached = null;
}

export function formatDateTime(value: string | Date | null | undefined): string {
  const date = toDate(value);

  return date ? formatters().dateTime.format(date) : EM_DASH;
}

export function formatDate(value: string | Date | null | undefined): string {
  const date = toDate(value);

  return date ? formatters().date.format(date) : EM_DASH;
}

/** Axis and tooltip labels: the year is noise on a 24-hour chart. */
export function formatChartTick(value: string | number | Date): string {
  const date = toDate(value);

  if (!date) {
    return "";
  }

  const { dayMonth, time } = formatters();

  return `${dayMonth.format(date)} ${time.format(date)}`;
}

const MINUTE = 60;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * "5 minutes ago" is what someone standing in front of the equipment wants to
 * know; an absolute timestamp makes them do the subtraction.
 */
export function formatRelative(value: string | Date | null | undefined): string {
  const date = toDate(value);

  if (!date) {
    return i18n.t("common:never");
  }

  const seconds = Math.round((date.getTime() - Date.now()) / 1_000);
  const magnitude = Math.abs(seconds);

  if (magnitude < MINUTE) {
    return i18n.t("common:justNow");
  }

  const { relative } = formatters();

  if (magnitude < HOUR) {
    return relative.format(Math.round(seconds / MINUTE), "minute");
  }

  if (magnitude < DAY) {
    return relative.format(Math.round(seconds / HOUR), "hour");
  }

  return relative.format(Math.round(seconds / DAY), "day");
}

export function formatTemperature(value: number | null | undefined): string {
  return value === null || value === undefined
    ? EM_DASH
    : `${formatters().number.format(value)} °C`;
}

export function formatHumidity(value: number | null | undefined): string {
  return value === null || value === undefined
    ? EM_DASH
    : `${formatters().number.format(value)} %`;
}

export function formatBattery(value: number | null | undefined): string {
  return value === null || value === undefined ? EM_DASH : `${Math.round(value)} %`;
}

/**
 * Millimetres, whole, and never converted to metres.
 *
 * The sensor reports millimetres and the thresholds are stored in them, so
 * rendering metres would put a unit conversion between what an operator reads
 * and what the alert engine compares. A tenth of a millimetre is also well
 * inside the sensor's own error, so the decimal would be invented precision.
 */
export function formatDistance(value: number | null | undefined): string {
  return value === null || value === undefined
    ? EM_DASH
    : `${Math.round(value).toLocaleString(getAppLocale())} mm`;
}

/**
 * A target range, or an honest blank when the asset is monitored without one
 * (ADR-045). Returning "— to — °C" would read like a broken range rather than
 * a deliberate absence.
 */
export function formatTemperatureRange(
  min: number | null | undefined,
  max: number | null | undefined,
): string {
  return formatRange(min, max, "°C");
}

export function formatHumidityRange(
  min: number | null | undefined,
  max: number | null | undefined,
): string {
  return formatRange(min, max, "%");
}

/**
 * Amperes, one decimal when the reading has one, never converted.
 *
 * The CT103 codec already reports amperes. Rendering milliamps would invent
 * a unit the threshold is not stored in (ADR-102).
 */
export function formatCurrent(value: number | null | undefined): string {
  return value === null || value === undefined
    ? EM_DASH
    : `${formatters().number.format(value)} A`;
}

export function formatCurrentRange(
  min: number | null | undefined,
  max: number | null | undefined,
): string {
  return formatRange(min, max, "A");
}

export function formatDistanceRange(
  min: number | null | undefined,
  max: number | null | undefined,
): string {
  if (min === null || min === undefined || max === null || max === undefined) {
    return EM_DASH;
  }

  const locale = getAppLocale();

  return `${Math.round(min).toLocaleString(locale)} ${i18n.t("common:to")} ${Math.round(max).toLocaleString(locale)} mm`;
}

/**
 * An occupancy reading in words.
 *
 * "Empty" rather than "No" or "False", because the question the sensor
 * answers is about the room and not about the bit. Null is a dash like every
 * other absent reading: the sensor said nothing, which is not the same as
 * saying nobody is there (ADR-069).
 */
export function formatOccupied(value: boolean | null | undefined): string {
  if (value === null || value === undefined) {
    return EM_DASH;
  }

  return value ? i18n.t("common:occupied") : i18n.t("common:empty");
}

/**
 * Spot leak probe state.
 *
 * "Dry" / "Leak" rather than true/false: the question is about the floor or
 * the base of a tank. Null is a dash — the sensor said nothing, which is not
 * the same as saying it is dry (ADR-077).
 */
export function formatLeaking(value: boolean | null | undefined): string {
  if (value === null || value === undefined) {
    return EM_DASH;
  }

  return value ? i18n.t("common:leaking") : i18n.t("common:dry");
}

/**
 * Magnetic contact state (Milesight EM300-MCS).
 *
 * "Open" / "Closed" rather than true/false. Null is a dash — the sensor said
 * nothing, which is not the same as saying the door is closed (ADR-078).
 */
export function formatDoorOpen(value: boolean | null | undefined): string {
  if (value === null || value === undefined) {
    return EM_DASH;
  }

  return value ? i18n.t("common:doorOpen") : i18n.t("common:doorClosed");
}

/**
 * The days a weekly schedule covers, as a person would say them.
 *
 * Runs of consecutive days are collapsed — "Mon to Fri" rather than five
 * abbreviations — because the overwhelmingly common schedule is one run and
 * spelling it out makes the interesting cases harder to spot. Days are
 * reordered into Monday-first sequence whatever order they arrive in.
 */
export function formatWeekdays(days: readonly number[]): string {
  const sorted = [...new Set(days)].filter((day) => day >= 1 && day <= 7).sort((a, b) => a - b);

  if (sorted.length === 0) {
    return EM_DASH;
  }

  if (sorted.length === 7) {
    return i18n.t("common:everyDay");
  }

  const name = (day: number) => i18n.t(`common:weekdays.${day}`);
  const to = i18n.t("common:to");
  const runs: number[][] = [];

  for (const day of sorted) {
    const last = runs.at(-1);

    if (last && day === last[last.length - 1]! + 1) {
      last.push(day);
    } else {
      runs.push([day]);
    }
  }

  return runs
    .map((run) =>
      run.length > 2
        ? `${name(run[0]!)} ${to} ${name(run[run.length - 1]!)}`
        : run.map((day) => name(day)).join(", "),
    )
    .join(", ");
}

function formatRange(
  min: number | null | undefined,
  max: number | null | undefined,
  unit: string,
): string {
  if (min === null || min === undefined || max === null || max === undefined) {
    return EM_DASH;
  }

  const { number } = formatters();

  return `${number.format(min)} ${i18n.t("common:to")} ${number.format(max)} ${unit}`;
}

/** DevEUIs are read off a label, so they are grouped in fours to be checkable. */
export function formatDevEui(devEui: string): string {
  return devEui.replace(/(.{4})(?=.)/g, "$1 ");
}

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const date = value instanceof Date ? value : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}
